import * as React from 'react'

import { KonvaEventObject } from 'konva/lib/Node'
import { observer } from 'mobx-react'

import { deliveryPinColors } from '~/client/src/shared/components/Konva/deliveryPinColors'
import KonvaPin from '~/client/src/shared/components/Konva/KonvaPin'
import { DELIVERY_PIN_ICON_NAME } from '~/client/src/shared/components/LogisticsMapView/deliveryPinPoints'
import SitemapElementsWrapper from '~/client/src/shared/components/SitemapElementsWrapper'
import MapViewItemBase from '~/client/src/shared/components/SitemapHelpers/models/MapViewItemBase'
import { getWorkflowGlyphPaths } from '~/client/src/shared/constants/workflowTypeGlyphs'
import Delivery from '~/client/src/shared/models/Delivery'
import User from '~/client/src/shared/models/User'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import DeliverySitemapPinStore from '~/client/src/shared/stores/ui/DeliverySitemapPin.store'

interface IProps {
  items: MapViewItemBase[]
  containerWidth: number
  containerHeight: number
  deliveriesByItemId: { [attrId: string]: Delivery[] }
  pinStore: DeliverySitemapPinStore
  user: User
  companiesStore: CompaniesStore
  // The one thing the two delivery maps disagree on, same as the GL pins.
  isPinSelected: (deliveries: Delivery[], item: MapViewItemBase) => boolean
  // The Konva event is forwarded, not swallowed: both maps stop the click bubbling to the sitemap
  // underneath, and react-konva delivers it as the handler's first argument.
  onPinClick: (
    deliveries: Delivery[],
    item: MapViewItemBase,
    event: KonvaEventObject<MouseEvent>,
  ) => void
  onPinTouchEnd: (
    deliveries: Delivery[],
    item: MapViewItemBase,
    event: KonvaEventObject<TouchEvent>,
  ) => void
}

/**
 * The delivery mini-pins on a whiteboard sitemap — the Konva path, still reached whenever a map
 * view is a raster sitemap rather than a globe.
 */
const KonvaDeliveryPins: React.FC<IProps> = observer(
  ({
    items,
    containerWidth,
    containerHeight,
    deliveriesByItemId,
    pinStore,
    user,
    companiesStore,
    isPinSelected,
    onPinClick,
    onPinTouchEnd,
  }) => (
    <SitemapElementsWrapper>
      {items.map(item => {
        // Normalised: shouldShowPin decides on an empty list, and the selection and label
        // predicates below would otherwise dereference undefined.
        const deliveries = deliveriesByItemId[item.id] || []
        if (!pinStore.shouldShowPin(item, deliveries)) {
          return null
        }
        const { x, y } = pinStore.getPinPosition(
          item,
          containerWidth,
          containerHeight,
        )

        const isCanceled = pinStore.shouldShowPinAsCanceled(deliveries)
        const isSelected = isPinSelected(deliveries, item)

        return (
          <KonvaPin
            key={item.id}
            {...deliveryPinColors({
              isDone: pinStore.shouldShowPinAsDone(deliveries),
              isCanceled,
              isAssigned: pinStore.shouldShowPinAsAssigned(deliveries, user),
              isSelected,
            })}
            glyph={getWorkflowGlyphPaths(DELIVERY_PIN_ICON_NAME)}
            isCanceled={isCanceled}
            isSelected={isSelected}
            text={pinStore.getPinLabel(deliveries, companiesStore)}
            shouldRenderCircle={pinStore.shouldRenderCircle(deliveries)}
            x={x}
            y={y}
            onClick={event => onPinClick(deliveries, item, event)}
            onTouchEnd={event => onPinTouchEnd(deliveries, item, event)}
          />
        )
      })}
    </SitemapElementsWrapper>
  ),
)

export default KonvaDeliveryPins
